// Shopping schedule and planner day rules.
import { addDays, startOfWeek, WEEKDAYS } from './dates';
import type { Day, Mealplan, PlanEntry, ShoppingSchedule } from '../types';
import { nanoid } from 'nanoid/non-secure';

/**
 * Next `count` shopping-trip days from `start` — port of Swift ShoppingSchedule.dates.
 * Weekday mode: that weekday in the current (Monday-first) week; if it has already passed,
 * move forward one interval.
 */
export function shoppingDates(schedule: ShoppingSchedule, start: Day, count = 52): Day[] {
  if (count <= 0) return [];
  if (schedule.kind === 'everyNDays') {
    const step = Math.max(schedule.n, 1);
    return Array.from({ length: count }, (_, i) => addDays(start, i * step));
  }
  const weeks = Math.max(schedule.everyNWeeks, 1);
  const monday = startOfWeek(start);
  // Swift: (weekday.calendarWeekday - weekdayOf(weekStart) + 7) % 7, weekStart is a Monday.
  const offset = (WEEKDAYS.indexOf(schedule.weekday) - 1 + 7) % 7;
  let first = addDays(monday, offset);
  if (first < start) first = addDays(first, 7 * weeks);
  return Array.from({ length: count }, (_, i) => addDays(first, i * 7 * weeks));
}

/** Adds trips without duplicates. Like Swift, existing trips are never removed by a schedule change. */
export function mergeTrips(existing: Day[], added: Day[]): Day[] {
  return [...new Set([...existing, ...added])].sort();
}

/** Past days are read-only in the planner. */
export function isPast(day: Day, today: Day): boolean {
  return day < today;
}

/**
 * ➕ "Apply from date…": copies a mealplan template into the planner starting at `start`.
 * Template day k lands on start + k. (Replaces Swift's hidden auto-created "Week N" mealplans.)
 */
export function applyMealplan(plan: Mealplan, start: Day): PlanEntry[] {
  return plan.entries.map(e => ({ id: nanoid(), date: addDays(start, e.day), slot: e.slot, recipeId: e.recipeId }));
}

/** Entries of one mealplan day, Swift MealplanView rule: dayOffset % cycleDays == offset. */
export function mealplanDay(plan: Mealplan, offset: number): Mealplan['entries'] {
  const cycle = Math.max(plan.cycleDays, 1);
  return plan.entries.filter(e => e.day % cycle === offset);
}
